/**
 * Portal Aktenwächter — PB-Client für `portal_akten` + `portal_akten_checklist`.
 * Pattern analog `verfahrensmotor/portalFristen.ts` (direkter PB-Pfad, Mandanten-Token).
 */

import { getPortalPocketBaseUrl } from '../../constants';
import {
  getAktenChecklistTemplate,
  VERTRAG_BERATUNG_CHECKLIST,
} from './aktenTemplates';
import type { AktenGuardProgress } from './aktenTemplates';
import {
  formatPortalAktenPbError,
  logPortalError,
  PORTAL_AKTEN_COLLECTION_MISSING_DE,
} from './portalErrorMessages';
import type {
  PortalAkteRecord,
  PortalAktenApiResult,
  PortalAktenChecklistRecord,
  PortalAktenChecklistStatus,
} from './portalAktenTypes';

/** Fortschritt direkt aus PB-Checkliste (inkl. eigener Punkte). */
export function computeAktenGuardProgressFromChecklist(
  items: PortalAktenChecklistRecord[],
): AktenGuardProgress {
  let requiredTotal = 0;
  let requiredDone = 0;
  let optionalTotal = 0;
  let optionalDone = 0;

  for (const item of items) {
    const done = item.status === 'done' || item.status === 'waived';
    if (item.required) {
      requiredTotal += 1;
      if (done) requiredDone += 1;
    } else {
      optionalTotal += 1;
      if (done) optionalDone += 1;
    }
  }

  return {
    requiredTotal,
    requiredDone,
    optionalTotal,
    optionalDone,
    allRequiredDone: requiredTotal > 0 && requiredDone === requiredTotal,
  };
}

export const PORTAL_AKTEN_COLLECTION = 'portal_akten';
export const PORTAL_AKTEN_CHECKLIST_COLLECTION = 'portal_akten_checklist';
export const PORTAL_AKTEN_PB_LIST_SORT = '-id';
export const PORTAL_AKTEN_CHECKLIST_SORT = 'sort_order';

/** Firma-PB ohne Akten-Collections: nach erstem 404 keine weiteren GETs (Demo-Network-Noise). */
let portalAktenCollectionMissing = false;

function skipPortalAktenPbFetch(): boolean {
  if (portalAktenCollectionMissing) return true;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname.trim().toLowerCase();
  // portal_akten ist Labor-Feature; Firma-Demo-PB hat die Collection typisch nicht → 404.
  return host === 'portal.arkorbit.at' || host === 'demo.arkorbit.at';
}

function normalizePbAuthHeader(authToken: string): string {
  const t = authToken.trim();
  if (!t) return '';
  return t.toLowerCase().startsWith('bearer ') ? t : `Bearer ${t}`;
}

function escapePbFilterString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export function normalizePortalAktePath(path?: string | null): string {
  return String(path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/\/$/, '')
    .toLowerCase();
}

/** Ordner- oder Dokumentpfad gegen gespeicherten `nc_path` einer Akte matchen. */
export function matchesPortalAkteNcPath(akteNcPath?: string | null, folderOrDocPath?: string | null): boolean {
  const akteKey = normalizePortalAktePath(akteNcPath);
  const folderKey = normalizePortalAktePath(folderOrDocPath);
  if (!akteKey || !folderKey) return false;
  if (akteKey === folderKey) return true;
  if (akteKey.endsWith(`/${folderKey}`) || folderKey.endsWith(`/${akteKey}`)) return true;
  const akteTail = akteKey.split('/').pop() ?? '';
  const folderTail = folderKey.split('/').pop() ?? '';
  return Boolean(akteTail && folderTail && akteTail === folderTail);
}

export function buildPortalAktenPbFilter(customerId: string): string {
  const cid = escapePbFilterString(customerId);
  return `(customer_id='${cid}')`;
}

export function buildPortalAktenChecklistPbFilter(customerId: string, akteId: string): string {
  const cid = escapePbFilterString(customerId);
  const aid = escapePbFilterString(akteId);
  return `(customer_id='${cid}' && akte_id='${aid}')`;
}

export function mapPbRecordToPortalAkte(r: Record<string, unknown>): PortalAkteRecord | null {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  if (!id) return null;

  const read = (key: string): string =>
    typeof r[key] === 'string' ? (r[key] as string).trim() : '';

  return {
    id,
    customer_id: read('customer_id'),
    matter_type: read('matter_type'),
    template_id: read('template_id'),
    title: read('title'),
    nc_path: read('nc_path') || undefined,
    mandant_label: read('mandant_label') || undefined,
    gegenseite_label: read('gegenseite_label') || undefined,
    vertragstyp: read('vertragstyp') || undefined,
    status: read('status') || 'in_progress',
    metadata_json: read('metadata_json') || undefined,
    external_key: read('external_key') || undefined,
    created: read('created') || undefined,
    updated: read('updated') || undefined,
  };
}

export function mapPbRecordToPortalAktenChecklistItem(
  r: Record<string, unknown>,
): PortalAktenChecklistRecord | null {
  const id = typeof r.id === 'string' ? r.id : String(r.id ?? '');
  if (!id) return null;

  const read = (key: string): string =>
    typeof r[key] === 'string' ? (r[key] as string).trim() : '';

  const statusRaw = read('status') as PortalAktenChecklistStatus;
  const status: PortalAktenChecklistStatus =
    statusRaw === 'done' || statusRaw === 'waived' ? statusRaw : 'open';

  return {
    id,
    customer_id: read('customer_id'),
    akte_id: read('akte_id'),
    item_id: read('item_id'),
    label: read('label'),
    required: Boolean(r.required),
    sort_order: typeof r.sort_order === 'number' ? r.sort_order : Number(r.sort_order) || 0,
    status,
    note: read('note') || undefined,
    completed_at: read('completed_at') || undefined,
    completed_by: read('completed_by') || undefined,
    created: read('created') || undefined,
    updated: read('updated') || undefined,
  };
}

async function pbFetchJson<T>(
  authToken: string,
  path: string,
  init?: RequestInit,
): Promise<PortalAktenApiResult<T>> {
  const token = normalizePbAuthHeader(authToken);
  if (!token) return { ok: false, error: 'Nicht angemeldet — bitte neu einloggen.' };

  const pbUrl = getPortalPocketBaseUrl().replace(/\/$/, '');
  try {
    const res = await fetch(`${pbUrl}${path}`, {
      ...init,
      headers: {
        ...(init?.headers ?? {}),
        Authorization: token,
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    let data: unknown = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) {
      const msg =
        data && typeof data === 'object' && 'message' in data
          ? String((data as { message?: string }).message)
          : `HTTP ${res.status}`;
      const formatted = formatPortalAktenPbError(res.status, msg);
      if (formatted.collectionMissing) {
        portalAktenCollectionMissing = true;
      }
      logPortalError(`portal_akten HTTP ${res.status}`, msg);
      return {
        ok: false,
        error: formatted.error,
        ...(formatted.collectionMissing ? { code: 'collection_missing' as const } : {}),
      };
    }
    return { ok: true, data: data as T };
  } catch {
    return { ok: false, error: 'Verbindung zu PocketBase fehlgeschlagen.' };
  }
}

export async function listPortalAktenForCustomer(
  customerId: string,
  authToken: string,
): Promise<PortalAkteRecord[]> {
  if (skipPortalAktenPbFetch()) return [];
  const filter = encodeURIComponent(buildPortalAktenPbFilter(customerId));
  const sort = encodeURIComponent(PORTAL_AKTEN_PB_LIST_SORT);
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records?filter=${filter}&sort=${sort}&perPage=100`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalAkte)
    .filter((x): x is PortalAkteRecord => Boolean(x));
}

export async function findPortalAkteForPath(
  customerId: string,
  authToken: string,
  folderOrDocPath?: string | null,
): Promise<PortalAkteRecord | null> {
  const all = await listPortalAktenForCustomer(customerId, authToken);
  if (!folderOrDocPath?.trim()) return all[0] ?? null;
  return all.find((a) => matchesPortalAkteNcPath(a.nc_path, folderOrDocPath)) ?? null;
}

export async function listChecklistItemsForAkte(
  customerId: string,
  authToken: string,
  akteId: string,
): Promise<PortalAktenChecklistRecord[]> {
  if (skipPortalAktenPbFetch()) return [];
  const filter = encodeURIComponent(buildPortalAktenChecklistPbFilter(customerId, akteId));
  const sort = encodeURIComponent(PORTAL_AKTEN_CHECKLIST_SORT);
  const result = await pbFetchJson<{ items?: Record<string, unknown>[] }>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records?filter=${filter}&sort=${sort}&perPage=200`,
  );
  if (!result.ok) return [];
  return (result.data.items ?? [])
    .map(mapPbRecordToPortalAktenChecklistItem)
    .filter((x): x is PortalAktenChecklistRecord => Boolean(x));
}

export async function updateChecklistItemStatus(
  authToken: string,
  recordId: string,
  status: PortalAktenChecklistStatus,
  completedBy?: string,
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const now = new Date().toISOString();
  const body: Record<string, unknown> = { status };
  if (status === 'done' || status === 'waived') {
    body.completed_at = now;
    if (completedBy?.trim()) body.completed_by = completedBy.trim();
  } else {
    body.completed_at = '';
    body.completed_by = '';
  }

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records/${recordId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped) return { ok: false, error: 'Antwort konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

export async function addChecklistItemToAkte(
  customerId: string,
  authToken: string,
  akteId: string,
  label: string,
  required = false,
): Promise<PortalAktenApiResult<PortalAktenChecklistRecord>> {
  const trimmed = label.trim();
  if (!trimmed) return { ok: false, error: 'Bezeichnung fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }

  const existing = await listChecklistItemsForAkte(customerId, authToken, akteId);
  const maxOrder = existing.reduce((m, row) => Math.max(m, row.sort_order), 0);
  const slugBase = trimmed
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/gi, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  const itemId = `custom_${slugBase || 'punkt'}_${Date.now().toString(36)}`;

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        akte_id: akteId,
        item_id: itemId,
        label: trimmed,
        required,
        sort_order: maxOrder + 1,
        status: 'open',
      }),
    },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAktenChecklistItem(result.data);
  if (!mapped) return { ok: false, error: 'Antwort konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}

export async function removeChecklistItemFromAkte(
  authToken: string,
  recordId: string,
): Promise<PortalAktenApiResult<null>> {
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const result = await pbFetchJson<unknown>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records/${recordId}`,
    { method: 'DELETE' },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  return { ok: true, data: null };
}

export interface CreatePortalAkteInput {
  title: string;
  nc_path?: string;
  mandant_label?: string;
  gegenseite_label?: string;
  vertragstyp?: string;
  matter_type?: string;
  template_id?: string;
  /** Nur diese Template-Punkte seeden (leer = alle). */
  checklist_item_ids?: string[];
}

/** Neue Akte Vertrag/Beratung anlegen + Checkliste aus Template seeden. */
export async function createPortalAkteVertragBeratung(
  customerId: string,
  authToken: string,
  input: CreatePortalAkteInput,
): Promise<PortalAktenApiResult<{ akte: PortalAkteRecord; checklistCount: number }>> {
  const title = input.title.trim();
  if (!title) return { ok: false, error: 'Akten-Titel fehlt.' };

  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }

  const templateId = input.template_id?.trim() || VERTRAG_BERATUNG_CHECKLIST.template_id;
  const matterType = input.matter_type?.trim() || VERTRAG_BERATUNG_CHECKLIST.matter_type;
  const template = getAktenChecklistTemplate(templateId) ?? VERTRAG_BERATUNG_CHECKLIST;

  const ncPath = input.nc_path?.trim()
    ? input.nc_path.trim().endsWith('/')
      ? input.nc_path.trim()
      : `${input.nc_path.trim()}/`
    : '';

  const akteResult = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records`,
    {
      method: 'POST',
      body: JSON.stringify({
        customer_id: customerId,
        matter_type: matterType,
        template_id: templateId,
        title,
        nc_path: ncPath || undefined,
        mandant_label: input.mandant_label?.trim() || undefined,
        gegenseite_label: input.gegenseite_label?.trim() || undefined,
        vertragstyp: input.vertragstyp?.trim() || undefined,
        status: 'in_progress',
        metadata_json: JSON.stringify({
          scenario: 'portal-guided-phase1',
          checklist_template: templateId,
          seeded_at: new Date().toISOString(),
        }),
      }),
    },
  );
  if (akteResult.ok === false) {
    return { ok: false, error: akteResult.error, code: akteResult.code };
  }

  const akte = mapPbRecordToPortalAkte(akteResult.data);
  if (!akte) return { ok: false, error: 'Akte konnte nicht gelesen werden.' };

  let checklistCount = 0;
  const selectedIds = input.checklist_item_ids?.length
    ? new Set(input.checklist_item_ids)
    : null;
  const templateItems = [...template.items]
    .sort((a, b) => a.order - b.order)
    .filter((item) => !selectedIds || selectedIds.has(item.id));

  if (!templateItems.length) {
    return { ok: false, error: 'Mindestens ein Checklisten-Punkt muss gewählt sein.' };
  }

  for (const item of templateItems) {
    const rowResult = await pbFetchJson<Record<string, unknown>>(
      authToken,
      `/api/collections/${PORTAL_AKTEN_CHECKLIST_COLLECTION}/records`,
      {
        method: 'POST',
        body: JSON.stringify({
          customer_id: customerId,
          akte_id: akte.id,
          item_id: item.id,
          label: item.label,
          required: item.required,
          sort_order: item.order,
          status: 'open',
        }),
      },
    );
    if (rowResult.ok) checklistCount += 1;
  }

  if (checklistCount === 0) {
    return { ok: false, error: 'Akte angelegt, Checkliste konnte nicht gespeichert werden.' };
  }

  return { ok: true, data: { akte, checklistCount } };
}

/** NC-Ordner/Pfad einer bestehenden Akte aktualisieren (Drag & Drop Verknüpfung). */
export async function updatePortalAkteNcPath(
  authToken: string,
  akteId: string,
  ncPath: string,
): Promise<PortalAktenApiResult<PortalAkteRecord>> {
  const trimmed = ncPath.trim();
  if (!trimmed) return { ok: false, error: 'Pfad fehlt.' };
  if (skipPortalAktenPbFetch()) {
    return {
      ok: false,
      error: PORTAL_AKTEN_COLLECTION_MISSING_DE,
      code: 'collection_missing',
    };
  }
  const normalized = trimmed.endsWith('/') ? trimmed : `${trimmed}/`;

  const result = await pbFetchJson<Record<string, unknown>>(
    authToken,
    `/api/collections/${PORTAL_AKTEN_COLLECTION}/records/${akteId}`,
    { method: 'PATCH', body: JSON.stringify({ nc_path: normalized }) },
  );
  if (result.ok === false) return { ok: false, error: result.error, code: result.code };
  const mapped = mapPbRecordToPortalAkte(result.data);
  if (!mapped) return { ok: false, error: 'Akte konnte nicht gelesen werden.' };
  return { ok: true, data: mapped };
}
